<script setup>
import router from '@/router';
import { useMutation } from '@tanstack/vue-query';
import { API } from '@/services/index';
import { INVITED_USER_MUTATION } from '@/services/auth/mutations/index.js';
import { useUserStore } from '@/stores/user.store.js';
import { useAuthStore } from '@/stores/auth.store.js';

const formRules = {
  firstName: (value) => !!value || 'First name is required',
  lastName: (value) => !!value || 'Last name is required',
  password: (value) => !!value || 'Password is required',
  password_confirmation: (value) => !!value || 'Password confirmation is required',
  passwordRegex: (value) =>
    useUserStore().passwordRegex.test(value) ||
    'Password must be a minimum of eight characters, at least one uppercase letter, one lowercase letter, one number and one special character',
  confirmPasswordRules: (value) => value === password.value || 'The password confirmation does not match.'
};

const props = defineProps({
  token: {
    type: String,
    required: true
  },
  redirectUrl: {
    type: String,
    required: true
  },
  errors: {
    type: String,
    default: null
  }
});

const resetForm = ref();
const isFormValid = ref(false);
const firstName = ref(null);
const lastName = ref(null);
const password = ref(null);
const passwordConfirmation = ref(null);
const isPasswordVisible = ref(false);
const isPasswordConfirmVisible = ref(false);
const isPasswordUpdated = ref(false);
const isLoading = ref(false);
const resetErrors = ref(props.errors ?? null);

watch(() => props.errors, (val) => {
  resetErrors.value = val;
});

const inviteCodeMutation = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql', INVITED_USER_MUTATION, payload);
  },
  onSuccess: (data) => {
    if (data.data.data.inviteCode) {
      isPasswordUpdated.value = true;
      useAuthStore().login(data.data.data.inviteCode.token);
      isLoading.value = false;
      router.push(props.redirectUrl);
    }
    if (data.data.errors) {
      // Helpers.showError(data.data.error.message);
      console.log(data.data.errors);
      resetErrors.value = data.data.errors[0]?.message != 'validation' ? data.data.errors[0]?.message : data.data.errors[0]?.extensions;
      isLoading.value = false;
    }
  },
  onError: (error) => {
    console.log('error', error);
    isLoading.value = false;
  }
});

const onSubmitHandler = () => {
  isLoading.value = true;
  inviteCodeMutation.mutateAsync({
    first_name: firstName.value,
    last_name: lastName.value,
    token: props.token,
    password: password.value,
    password_confirmation: passwordConfirmation.value
  });
};
</script>

<template>
  <div>
    <div v-if="resetErrors" class="tw-mt-2 tw-text-center tw-text-xs tw-font-bold tw-text-red-700">
      <template v-if="resetErrors.validation">
        <div v-for="(messages, field) in resetErrors.validation" :key="field">
          <div v-for="(msg, i) in messages" :key="i">{{ msg }}</div>
        </div>
      </template>
      <template v-else-if="typeof resetErrors === 'string'">{{ resetErrors }}</template>
    </div>
    <div v-if="isPasswordUpdated" class="tw-mt-5 tw-text-center tw-text-[0.875rem] tw-font-semibold tw-text-green-700">
      Your password has been successfully set.
    </div>
    <v-form class="mt-5" ref="resetForm" v-model="isFormValid" @submit.prevent="onSubmitHandler">
      <div class="tw-flex tw-w-full tw-flex-col tw-space-y-3">
        <div class="tw-flex tw-w-full tw-flex-col">
          <v-text-field
            v-model="firstName"
            :rules="[formRules.firstName]"
            variant="outlined"
            color="primary-blue"
            placeholder="First Name"
            hide-details="auto"
            density="comfortable"
            :disabled="isLoading"
          >
          </v-text-field>
        </div>
        <div class="tw-flex tw-w-full tw-flex-col">
          <v-text-field
            v-model="lastName"
            :rules="[formRules.lastName]"
            variant="outlined"
            color="primary-blue"
            placeholder="Last Name"
            hide-details="auto"
            density="comfortable"
            :disabled="isLoading"
          >
          </v-text-field>
        </div>
        <div class="tw-flex tw-w-full tw-flex-col">
          <v-text-field
            v-model="password"
            :type="isPasswordVisible ? 'text' : 'password'"
            :rules="[formRules.password, formRules.passwordRegex]"
            variant="outlined"
            color="primary-blue"
            placeholder="Password*"
            autocomplete="new-password"
            hide-details="auto"
            density="comfortable"
            :disabled="isLoading"
          >
            <template v-slot:append-inner>
              <v-icon color="primary-blue" @click="isPasswordVisible = !isPasswordVisible" :style="password ? 'opacity: 100%' : ''">
                {{ isPasswordVisible ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </template>
          </v-text-field>
        </div>
        <div class="tw-flex tw-w-full tw-flex-col">
          <v-text-field
            v-model="passwordConfirmation"
            :type="isPasswordConfirmVisible ? 'text' : 'password'"
            :rules="[formRules.password_confirmation, formRules.confirmPasswordRules]"
            variant="outlined"
            color="primary-blue"
            placeholder="Password Confirm*"
            autocomplete="none"
            hide-details="auto"
            density="comfortable"
            :disabled="isLoading"
          >
            <template v-slot:append-inner>
              <v-icon
                color="primary-blue"
                @click="isPasswordConfirmVisible = !isPasswordConfirmVisible"
                :style="passwordConfirmation ? 'opacity: 100%' : ''"
              >
                {{ isPasswordConfirmVisible ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </template>
          </v-text-field>
        </div>
      </div>
      <div class="tw-mt-2 tw-flex tw-w-full">
        <v-btn
          color="primary-blue"
          size="large"
          rounded="lg"
          block
          flat
          class="tw-font-bold tw-capitalize"
          style="letter-spacing: 0px"
          type="submit"
          :loading="isLoading"
          :disabled="isLoading || !isFormValid"
        >
          Set Password
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<style lang="css" scoped></style>
